import { useRouter } from "next/router";
import React, { useState } from "react";
import { FormEvent } from "react";

import { resetPassword } from "../../../../api/users/resetPassword";
import { translationsContent } from "../../../../localization/translations";
import { useLanguage } from "../../../../store/lang.context";
import { useLoader } from "../../../../store/loader.context";
import { translate } from "../../../../utils/translate";
import { passwordSchema } from "../../../../utils/validations";
import Button from "../../../ui/button/SimpleButton";
import Input from "../../../ui/input/SimpleInput";
import SimpleModal from "../../../ui/modal/SimpleModal";
import { SubmitObjectType } from "../types/submit.type";
import classes from "./form.module.scss";

export type PasswordResetModelType = {
  password: string;
  confirmPassword: string;
};

const passwordObject = {
  password: { value: "", isValid: true },
  confirmPassword: { value: "", isValid: true },
};

interface Props {
  setPasswordResetIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const PasswordResetForm = ({ setPasswordResetIsOpen }: Props) => {
  const router = useRouter();
  const { language } = useLanguage();
  const { uid, token } = router.query;
  const { setLoader } = useLoader();
  const [errorPF, setErrorPF] = useState(false);
  const [errorPM, setErrorPM] = useState(false);
  const [errorP, setErrorP] = useState(false);
  const [formData, setFormData] = useState(passwordObject);

  const handleInputChange = (
    identifier: keyof PasswordResetModelType,
    value: string | boolean
  ) => {
    setFormData((FormDataPreviousState) => ({
      ...FormDataPreviousState,
      [identifier]: {
        value,
        isValid: !passwordSchema.validate({ [identifier]: value }).error,
      },
    }));
  };

  const handleRouting = () => {
    switch (language) {
      case "fr":
        router.push("/?reset=true");
        break;
      case "en":
        router.push("/en/?reset=true");
        break;
      case "es":
        router.push("/es/?reset=true");
        break;
      case "nl":
        router.push("/nl/?reset=true");
        break;
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    setErrorP(false);
    setErrorPF(false);

    setLoader(true);
    const input = {
      new_password1: formData.password.value,
      new_password2: formData.confirmPassword.value,
      uid: uid as string,
      token: token as string,
    };
    const validate = passwordSchema.validate(input);

    if (input.new_password1 !== input.new_password2) {
      setErrorPM(true);
      setLoader(false);
      return;
    }

    setErrorPM(false);

    if (validate.error) {
      for (const key in formData) {
        const validationItem = formData[key as keyof PasswordResetModelType];
        const tmpValidate = passwordSchema.validate({
          [key]: validationItem.value,
        });
        if (tmpValidate.error)
          setFormData((prevState) => ({
            ...prevState,
            [key]: {
              value: validationItem.value,
              isValid: false,
            },
          }));
      }
      setLoader(false);
      return;
    }

    const isReset = await resetPassword(input);
    if (isReset.success) {
      handleRouting();
      setErrorP(false);
    } else {
      if (isReset.error && isReset.error.code === "token_not_valid") {
        setErrorPF(true);
      } else {
        setErrorP(true);
        setErrorPF(false);
      }
    }
    setLoader(false);
  };

  return (
    <>
      <SimpleModal setModalIsOpen={setPasswordResetIsOpen}>
        <h2 className={classes.heading}>
          Veuillez saisir un nouveau mot de passe
        </h2>
        <div className={classes.container} id="signUpPage">
          <form
            onSubmit={(e) => {
              handleSubmit(e);
            }}
          >
            <Input<SubmitObjectType, "password">
              label="Mot de passe*"
              isValid={formData.password.isValid}
              error={errorPF}
              type="password"
              identifier="password"
              name="password"
              placeholder={`${translate(
                translationsContent,
                "newPassword",
                language
              )}`}
              required
              onChange={handleInputChange}
            />
            <Input<SubmitObjectType, "confirmPassword">
              label="Confirmer mot de passe*"
              isValid={formData.confirmPassword.isValid}
              error={errorPF}
              name="confirmPassword"
              type="password"
              identifier="confirmPassword"
              placeholder={`${translate(
                translationsContent,
                "confirmNewPassword",
                language
              )}`}
              required
              onChange={handleInputChange}
            />
            <div className={classes.button}>
              {errorPF && (
                <p style={{ color: "red" }}>
                  {translate(
                    translationsContent,
                    "passwordTokenError",
                    language
                  )}
                </p>
              )}
              {errorPM && (
                <p style={{ color: "red" }}>
                  {translate(translationsContent, "passwordMismatch", language)}
                </p>
              )}
              {errorP && (
                <p style={{ color: "red" }}>
                  {translate(translationsContent, "passwordError", language)}
                </p>
              )}
              <Button
                label="SE CONNECTER"
                disabled={
                  formData.confirmPassword.value.length === 0 ||
                  formData.password.value.length === 0
                }
              />
            </div>
          </form>
        </div>
      </SimpleModal>
    </>
  );
};

export default PasswordResetForm;
